/**
 * DYNAMIC SHIPPING COSTS - MARKET-AWARE VERSION
 *
 * This handles dynamic shipping cost display based on Shopify Markets and location detection.
 * Now integrates with the three-tier market system (Germany, EU, Non-EU).
 *
 * FEATURES:
 * - Market-aware shipping cost display (Germany/EU/Non-EU)
 * - Location-based currency formatting (€ for most countries, CHF for Switzerland)
 * - Graceful fallback to default costs
 * - Comprehensive logging for debugging
 *
 * REQUIREMENTS:
 * - locator.js must be loaded first
 * - custom-global.js for market detection
 * - Settings: cart_delivery_cost_germany, ww_dynamic_shipping_costs
 * - Data attribute format: "de: 4.95, ch: 14.90, default: 14.90" (combines both settings)
 */

(() => {
  class ShippingTextUpdater {
    constructor() {
      console.group('💰 [Shipping Handler] Initializing market-aware shipping cost updater...');

      this.shippingTextElements = document.querySelectorAll('[data-shipping-text]');
      console.log(`🔍 Found ${this.shippingTextElements.length} shipping text elements`);

      if (this.shippingTextElements.length === 0) {
        console.log('⚠️ No shipping text elements found, skipping initialization');
        console.groupEnd();
        return;
      }

      // Parse shipping costs from first element (they should all have the same data)
      this.shippingCosts = this.parseShippingCosts(this.shippingTextElements[0]);
      this.currentCountry = 'default';
      this.currentMarketCategory = 'non-eu';

      console.log('📊 Initial shipping costs:', this.shippingCosts);

      // Listen for location updates from locator.js
      window.addEventListener('userLocationDetected', (event) => {
        console.log('📍 [Shipping Handler] Location detected:', event.detail);
        this.updateShippingText(event.detail);
      });

      // Listen for market category changes from custom-global.js
      window.addEventListener('marketCategoryChanged', (event) => {
        console.log('🏪 [Shipping Handler] Market category changed:', event.detail);
        this.currentMarketCategory = event.detail.category;
        // Refresh shipping display based on current location
        const storedLocation = this.getStoredLocation();
        if (storedLocation) {
          this.updateShippingText(storedLocation);
        }
      });

      // Try to update immediately if location is already stored
      const storedLocation = this.getStoredLocation();
      if (storedLocation) {
        console.log('🔄 Updating with stored location:', storedLocation);
        this.updateShippingText(storedLocation);
      } else {
        console.log('⚠️ No stored location found');
      }

      console.groupEnd();
    }

    /**
     * Refresh element references (for when DOM changes)
     */
    refreshElements() {
      const previousCount = this.shippingTextElements.length;
      this.shippingTextElements = document.querySelectorAll('[data-shipping-text]');
      console.log(`🔄 [Shipping Handler] Refreshed elements: ${previousCount} → ${this.shippingTextElements.length}`);
    }

    /**
     * Parse shipping costs from data attribute
     */
    parseShippingCosts(element) {
      if (!element || !element.dataset.shippingCosts) {
        console.warn('⚠️ [Shipping Handler] No shipping costs data found, using default');
        return { default: 14.90 };
      }

      const costsString = element.dataset.shippingCosts;
      console.log(`📋 [Shipping Handler] Parsing costs string: "${costsString}"`);
      const costs = {};

      // Split by comma and parse each pair
      const pairs = costsString.split(',').map(pair => pair.trim());
      console.log(`🔍 Parsed ${pairs.length} cost pairs:`, pairs);

      for (const pair of pairs) {
        if (pair.includes(':')) {
          const [country, cost] = pair.split(':').map(s => s.trim());
          const parsedCost = parseFloat(cost);
          costs[country] = parsedCost;
          console.log(`💶 Added cost: ${country} → €${parsedCost}`);
        } else {
          console.warn(`⚠️ Invalid cost pair format: "${pair}"`);
        }
      }

      console.log('💰 Final parsed costs:', costs);
      return costs;
    }

    /**
     * Get stored location from localStorage
     */
    getStoredLocation() {
      try {
        const stored = localStorage.getItem('userCountryCode');
        return stored ? JSON.parse(stored) : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Format money amount with proper currency symbol
     */
    formatMoney(amount, countryCode = 'de') {
      const amountInCents = Math.round(amount * 100);
      const formatted = (amountInCents / 100).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      // Use CHF for Switzerland, EUR for others
      const currency = countryCode.toLowerCase() === 'ch' ? 'CHF' : '€';

      return `${formatted}${currency}`;
    }

    /**
     * Get shipping amount for a country
     */
    getShippingAmount(countryCode) {
      if (!this.shippingCosts || Object.keys(this.shippingCosts).length === 0) {
        return 14.90;
      }

      const lowerCountryCode = countryCode.toLowerCase();

      // Try exact match first
      if (this.shippingCosts[lowerCountryCode] !== undefined) {
        return this.shippingCosts[lowerCountryCode];
      }

      // Fall back to default
      return this.shippingCosts.default || 14.90;
    }

    /**
     * Update shipping text elements with new location data
     */
    updateShippingText(locationData) {
      console.group('💰 [Shipping Handler] Updating shipping text display...');

      // Refresh element references in case DOM has changed
      this.refreshElements();

      if (!locationData || this.shippingTextElements.length === 0) {
        console.log('⚠️ No location data or shipping elements available');
        console.groupEnd();
        return;
      }

      // Clean and normalize the location data
      const cleanedLocation = typeof locationData === 'string'
        ? locationData.trim()
        : locationData.toString().trim();

      // Resolve country code (handle both direct codes and objects)
      const countryCode = cleanedLocation.toLowerCase();
      console.log(`🌍 Processing location: "${locationData}" → country code: "${countryCode}"`);

      // Get current market category from DOM or previous detection
      let marketCategory = this.currentMarketCategory;
      const marketElements = document.querySelectorAll('[data-market-category]');
      if (marketElements.length > 0) {
        marketCategory = marketElements[0].getAttribute('data-market-category');
        console.log(`🏪 Market category from DOM: "${marketCategory}"`);
      }

      // Get shipping amount for this country
      const amount = this.getShippingAmount(countryCode);
      console.log(`💶 Shipping amount for "${countryCode}": €${amount}`);

      // Format the amount with proper currency
      const formattedAmount = this.formatMoney(amount, countryCode);
      console.log(`💰 Formatted amount: "${formattedAmount}"`);

      // Update all stored elements
      let updatedCount = 0;
      for (let i = 0; i < this.shippingTextElements.length; i++) {
        const element = this.shippingTextElements[i];
        if (element && element.isConnected) {
          const previousText = element.textContent;
          element.textContent = formattedAmount;

          // Log market-specific elements
          const elementMarket = element.getAttribute('data-market-category');
          if (elementMarket) {
            console.log(`🎯 Updated element with market "${elementMarket}": "${previousText}" → "${formattedAmount}"`);
          } else {
            console.log(`🔄 Updated element: "${previousText}" → "${formattedAmount}"`);
          }
          updatedCount++;
        }
      }

      console.log(`✅ Updated ${updatedCount}/${this.shippingTextElements.length} shipping text elements`);
      console.log(`📊 Update summary:`, {
        country: countryCode,
        marketCategory,
        amount,
        formattedAmount,
        elementsUpdated: updatedCount
      });

      // Update current country
      this.currentCountry = countryCode;
      this.currentMarketCategory = marketCategory;

      console.groupEnd();
    }
  }

  // Initialize when DOM is ready
  function initializeShippingSystem() {
    console.log('🚀 [Shipping Handler] Initializing shipping system...');

    // Create global shipping updater instance
    window.globalShippingUpdater = new ShippingTextUpdater();

    if (!window.globalShippingUpdater.shippingTextElements || window.globalShippingUpdater.shippingTextElements.length === 0) {
      console.log('⚠️ [Shipping Handler] No shipping elements found, skipping observer setup');
      return;
    }

    // Set up MutationObserver to watch for cart drawer changes
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const hasShippingElements = addedNodes.some(node =>
            node.nodeType === 1 && // Element node
            node.querySelector &&
            node.querySelector('[data-shipping-text]')
          );

          if (hasShippingElements) {
            shouldUpdate = true;
            break;
          }
        }
      }

        if (shouldUpdate) {
          console.log('🔄 [Shipping Handler] DOM mutation detected, updating shipping display...');
          // Small delay to ensure DOM is fully updated
          setTimeout(() => {
            if (window.globalShippingUpdater) {
              window.globalShippingUpdater.shippingTextElements = document.querySelectorAll('[data-shipping-text]');
              const storedLocation = window.globalShippingUpdater.getStoredLocation();
              if (storedLocation) {
                console.log('🔄 [Shipping Handler] Updating after DOM mutation with location:', storedLocation);
                window.globalShippingUpdater.updateShippingText(storedLocation);
              }
            }
          }, 50);
        }
    });

    // Observe cart drawer and main cart
    const cartDrawer = document.querySelector('cart-drawer');
    const mainCart = document.querySelector('#main-cart-footer');

    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }

    if (mainCart) {
      observer.observe(mainCart, {
        childList: true,
        subtree: true
      });
    }

    // Listen for cart drawer open events
    window.addEventListener('cart-drawer:open', () => {
      setTimeout(() => {
        if (window.globalShippingUpdater) {
          // Force refresh element references
          window.globalShippingUpdater.shippingTextElements = document.querySelectorAll('[data-shipping-text]');
          const storedLocation = window.globalShippingUpdater.getStoredLocation();
          if (storedLocation) {
            window.globalShippingUpdater.updateShippingText(storedLocation);
          }
        }
      }, 500);
    });

    // Expose comprehensive debugging tools
    window.debugShippingHandler = {
      testLocation: (countryCode) => {
        console.log(`🧪 [Debug] Testing shipping location: "${countryCode}"`);
        if (window.globalShippingUpdater) {
          window.globalShippingUpdater.updateShippingText(countryCode);
        } else {
          console.error('❌ [Debug] globalShippingUpdater not available');
        }
      },

      getShippingElements: () => {
        const elements = document.querySelectorAll('[data-shipping-text]');
        console.log(`🔍 [Debug] Found ${elements.length} shipping elements:`, elements);
        return Array.from(elements).map(el => ({
          element: el,
          text: el.textContent,
          costs: el.dataset.shippingCosts,
          marketCategory: el.dataset.marketCategory
        }));
      },

      getCurrentState: () => {
        const state = {
          hasUpdater: !!window.globalShippingUpdater,
          elementCount: window.globalShippingUpdater?.shippingTextElements?.length || 0,
          currentCountry: window.globalShippingUpdater?.currentCountry,
          currentMarketCategory: window.globalShippingUpdater?.currentMarketCategory,
          storedLocation: window.globalShippingUpdater?.getStoredLocation?.(),
          shippingCosts: window.globalShippingUpdater?.shippingCosts
        };
        console.log('📊 [Debug] Current shipping handler state:', state);
        return state;
      },

      testMarketScenarios: () => {
        console.group('🧪 [Debug] Testing all market scenarios...');
        const testCases = [
          { country: 'de', expectedCategory: 'germany' },
          { country: 'fr', expectedCategory: 'eu' },
          { country: 'dk', expectedCategory: 'eu' },
          { country: 'nl', expectedCategory: 'eu' },
          { country: 'ch', expectedCategory: 'non-eu' },
          { country: 'us', expectedCategory: 'non-eu' }
        ];

        testCases.forEach(({ country, expectedCategory }) => {
          console.log(`Testing ${country} (expected: ${expectedCategory})`);
          window.debugShippingHandler.testLocation(country);
        });
        console.groupEnd();
      }
    };

    // Legacy function for backward compatibility
    window.testShippingLocation = window.debugShippingHandler.testLocation;

    console.log('🛠️ [Debug] Shipping handler debugging tools available at window.debugShippingHandler');
    console.log('✅ [Shipping Handler] Initialization complete!');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeShippingSystem);
  } else {
    initializeShippingSystem();
  }
})();
